import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface SelectedClient {
  id: number;
  name: string;
}

@Component({
  selector: 'app-client-chip-input',
  templateUrl: './client-chip-input.component.html',
  styleUrls: ['./client-chip-input.component.css'],
})
export class ClientChipInputComponent implements OnInit, OnDestroy {
  @ViewChild('chipInput') chipInputRef!: ElementRef<HTMLInputElement>;

  @Input() placeholder: string = 'Search clients…';
  /** Scope the search to one branch (omit for no branch filter). */
  @Input() branchId?: number;
  /** Scope the search to one client type, e.g. 'INDIVIDUAL' (omit for no filter). */
  @Input() clientType?: string;
  /**
   * Characters required before searching. 0 also loads results immediately
   * on init (an empty filter), for callers that want a browsable list rather
   * than a type-ahead. Defaults to 2, unchanged for existing callers.
   */
  @Input() minSearchLength: number = 2;

  @Output() clientsChanged = new EventEmitter<SelectedClient[]>();

  selectedClients: SelectedClient[] = [];
  searchResults: SelectedClient[] = [];
  isSearching: boolean = false;
  searchTerm: string = '';

  private searchInput$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  private readonly clientsUrl = `${environment.baseUrl}${environment.coreUrl}clients`;

  constructor(private http: HttpClient) {
    this.searchInput$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap(term => {
          if (term.length < this.minSearchLength) {
            this.searchResults = [];
            this.isSearching = false;
            return [];
          }
          this.isSearching = true;
          const scopeParams = [
            this.branchId ? `branchId=${this.branchId}` : '',
            this.clientType ? `clientType=${this.clientType}` : '',
          ]
            .filter(Boolean)
            .join('&');
          return this.http.get<any>(
            `${this.clientsUrl}?page=0&pageSize=20&state=APPROVED&sortField=last_update_time&sortOrder=DESC${scopeParams ? '&' + scopeParams : ''}&filter=${encodeURIComponent(term)}`
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          this.isSearching = false;
          const items: any[] = response?.data?.content ?? response?.data ?? [];
          const selectedIds = new Set(this.selectedClients.map(client => client.id));
          this.searchResults = items
            .filter((item: any) => !selectedIds.has(item.id))
            .map((item: any) => ({
              id: item.id,
              name: `${item.first_name ?? ''} ${item.last_name ?? ''}`.trim(),
            }));
        },
        error: () => {
          this.isSearching = false;
          this.searchResults = [];
        },
      });
  }

  ngOnInit(): void {
    if (this.minSearchLength <= 0) this.searchInput$.next('');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  displayEmpty = () => '';

  onSearchChange(term: string): void {
    this.searchInput$.next(term);
  }

  onSelect(client: SelectedClient): void {
    if (!this.selectedClients.find(selected => selected.id === client.id)) {
      this.selectedClients = [...this.selectedClients, client];
      this.clientsChanged.emit(this.selectedClients);
    }
    this.searchTerm = '';
    this.searchResults = [];
    setTimeout(() => this.chipInputRef?.nativeElement.focus());
  }

  removeClient(client: SelectedClient): void {
    this.selectedClients = this.selectedClients.filter(selected => selected.id !== client.id);
    this.clientsChanged.emit(this.selectedClients);
  }

  onBackspace(inputEl: HTMLInputElement): void {
    if (inputEl.value !== '' || this.selectedClients.length === 0) return;
    this.selectedClients = this.selectedClients.slice(0, -1);
    this.clientsChanged.emit(this.selectedClients);
  }

  reset(): void {
    this.selectedClients = [];
    this.searchResults = [];
    this.searchTerm = '';
  }

  setClients(clients: SelectedClient[]): void {
    this.selectedClients = [...clients];
    this.clientsChanged.emit(this.selectedClients);
  }
}
